import axios from "axios";

export const api = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
  withCredentials: true,
  timeout: 300000,
  headers: {
    // CSRF defence-in-depth: backend rejects state-changing multipart
    // uploads without this header. Cross-site drive-by <form> POSTs cannot
    // set custom headers without a CORS preflight (which our non-wildcard
    // CORS blocks). See SEC-002.
    "X-Requested-With": "XMLHttpRequest",
  },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("eg_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Retry-with-cap on transient failures for GETs.
// - Retries: network errors, 5xx, and a SINGLE 401 blip (auth race during job polling)
// - Backoff: 300ms, 900ms
// - Never retries: non-GET (mutations must not be auto-repeated), 4xx other than a single 401
// - After retries exhaust: rejects normally so callers can show error UI
const MAX_RETRIES = 2;

function shouldRetry(error, attempt) {
  if (attempt >= MAX_RETRIES) return false;
  const cfg = error.config || {};
  const method = (cfg.method || "get").toLowerCase();
  if (method !== "get") return false;
  const status = error.response?.status;
  if (!error.response) return true; // network error / timeout
  if (status >= 500 && status < 600) return true;
  // Allow ONE retry on 401 to survive an in-flight auth race, and only on the first attempt.
  if (status === 401 && attempt === 0) return true;
  return false;
}

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const cfg = error.config;
    if (cfg && !cfg.__retryDisabled) {
      cfg.__retryCount = cfg.__retryCount || 0;
      if (shouldRetry(error, cfg.__retryCount)) {
        cfg.__retryCount += 1;
        const delayMs = 300 * Math.pow(3, cfg.__retryCount - 1); // 300ms, 900ms
        await new Promise((r) => setTimeout(r, delayMs));
        return api.request(cfg);
      }
    }

    const status = error.response?.status;
    const url = error.config?.url || "";
    if (status === 401 && !url.includes("/auth/")) {
      // Retries exhausted on 401 → treat as real auth failure. Purge token
      // and send to /login (only if we're not already on a public page).
      localStorage.removeItem("eg_token");
      const path = window.location.pathname;
      if (path !== "/" && path !== "/login" && path !== "/signup") {
        window.location.href = "/login";
      }
    }
    return Promise.reject(error);
  }
);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function errMsg(e) {
  return formatApiErrorDetail(e?.response?.data?.detail) || e?.message || "Request failed";
}

/**
 * POST with capped exponential backoff for transient failures.
 * Retries: network errors + 5xx responses.
 * Does NOT retry: 4xx (auth failures / validation) — those fail fast.
 * Callers should format their own error UI on final rejection.
 */
export async function postWithRetry(url, body, { attempts = 3, backoffMs = [400, 1200] } = {}) {
  let lastErr;
  for (let i = 0; i < attempts; i++) {
    try {
      return await api.post(url, body);
    } catch (e) {
      lastErr = e;
      const status = e.response?.status;
      // Fail fast on definite client/auth errors.
      if (status && status >= 400 && status < 500) throw e;
      if (i < attempts - 1) {
        await new Promise((r) => setTimeout(r, backoffMs[i] ?? backoffMs[backoffMs.length - 1]));
      }
    }
  }
  throw lastErr;
}

/** Classify a caught error into a user-facing message for auth POST calls. */
export function authErrorMsg(e, fallback = "Something went wrong. Please try again.") {
  const status = e?.response?.status;
  const backend = errMsg(e);
  if (status && status >= 400 && status < 500) return backend || fallback;
  if (status && status >= 500) return "Sign-in is temporarily unavailable. Please retry in a moment.";
  if (!e?.response) return "We couldn't reach the sign-in service. Check your connection and retry.";
  return backend || fallback;
}

export async function runJob(postFn) {
  const { data } = await postFn();
  const jobId = data.job_id;
  for (let i = 0; i < 240; i++) {
    await new Promise((r) => setTimeout(r, 3000));
    const { data: job } = await api.get(`/jobs/${jobId}`);
    if (job.status === "done") return job;
    if (job.status === "error") throw new Error(job.error || "Job failed");
  }
  throw new Error("Timed out waiting for the AI — please try again");
}
